import React, { useState } from "react";
import { verificationAPI } from "../../../services/verificationAPI";
import "../Education/styles/EducationCard.css";


const EmploymentCard = ({ employment, onEdit, onDelete, isSelected }) => {
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(
    employment.verification?.status
  );
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // local modal state for resend confirmation (same pattern as EducationCard)
  const [resendOpen, setResendOpen] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  const getDateRange = () => {
    const startDate = formatDate(employment.startDate);
    if (employment.isOngoing) return `${startDate} - Present`;
    const endDate = formatDate(employment.endDate);
    return `${startDate} - ${endDate}`;
  };

  const getVerificationStatus = () => {
    const status =
      verificationStatus ||
      employment.verification?.status ||
      employment.verificationStatus ||
      "NOT_REQUESTED";

    const statusConfig = {
      PENDING: { label: "Verification Pending", class: "pending", icon: "⏳" },
      IN_PROGRESS: { label: "In Progress", class: "in-progress", icon: "🔄" },
      VALIDATED: { label: "Verified", class: "verified", icon: "✅" },
      REJECTED: { label: "Verification Rejected", class: "rejected", icon: "❌" },
      NOT_REQUESTED: { label: "Not Requested", class: "not-requested", icon: "⚪" },
    };

    return statusConfig[status] || statusConfig.NOT_REQUESTED;
  };

  const verificationStatusData = getVerificationStatus();

  // open resend confirm modal
  const handleRequestVerificationClick = () => {
    if (!employment.verifierEmail) {
      alert("Please add a verifier email address before requesting verification.");
      return;
    }
    setResendOpen(true);
  };

  const closeResendConfirm = () => setResendOpen(false);

  const confirmResend = async () => {
    setResendOpen(false);
    setVerificationLoading(true);
    setError("");
    setSuccess("");

    try {
      await verificationAPI.requestEmploymentVerification(employment.id);
      setVerificationStatus("PENDING");

      const message =
        verificationStatusData.class === "not-requested"
          ? "Employment verification request sent successfully!"
          : "Employment verification request resent successfully!";

      setSuccess(message);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to request verification");
    } finally {
      setVerificationLoading(false);
    }
  };

  return (
    <div className={`education-card ${isSelected ? "selected" : ""}`}>
      {/* Top row: main info + actions */}
      <div className="education-row">
        <div className="education-main">
          <h3 className="education-degree">{employment.jobTitle}</h3>

          <p className="education-institution">{employment.company}</p>

          {/* Verification badge (same placement as EducationCard) */}
          <div className="verification-status">
            <span className={`verification-badge ${verificationStatusData.class}`}>
              {verificationStatusData.icon} {verificationStatusData.label}
            </span>
          </div>

          {/* Optional details like EducationCard (small text lines) */}
          {(employment.location || employment.employmentType) && (
            <p className="education-details">
              {employment.location ? employment.location : ""}
              {employment.location && employment.employmentType ? " • " : ""}
              {employment.employmentType ? employment.employmentType : ""}
            </p>
          )}

          {employment.responsibilities && (
            <p className="education-field">
              {employment.responsibilities.length > 150
                ? `${employment.responsibilities.substring(0, 150)}...`
                : employment.responsibilities}
            </p>
          )}

          {/* Verifier lines (same style as EducationCard) */}
          {employment.verifierEmail && (
            <div className="verifier-info">
              <div>
                <strong>Verifier:</strong>
              </div>
              <div className="verifier-email">{employment.verifierEmail}</div>
            </div>
          )}
        </div>

        <div className="education-actions">
          <button
  onClick={onEdit}
  className="btn-icon btn-edit"
  title="Edit Employment"
>
  <svg xmlns="www.w3.org" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
</button>

<button
  onClick={onDelete}
  className="btn-icon btn-delete"
  title="Delete Employment"
>
  <svg xmlns="www.w3.org" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="red" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
</button>
        </div>
      </div>

      {/* Footer row: dates left, resend button right */}
      <div className="education-footer">
        <div className="education-dates">{getDateRange()}</div>

        <button
          type="button"
          className="resend-btn"
          onClick={handleRequestVerificationClick}
          disabled={verificationLoading}
        >
          {verificationLoading
            ? "Sending..."
            : verificationStatusData.class === "not-requested"
            ? "Send Request"
            : "Resend Request"}
        </button>
      </div>

      {/* Messages under card */}
      {success && <div className="message success-message">{success}</div>}
      {error && <div className="message error-message">{error}</div>}

      {/* Resend confirm modal (same pattern as EducationCard) */}
      {resendOpen && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <h3 className="confirm-title">
              {verificationStatusData.class === "not-requested"
                ? "Send verification request"
                : "Resend verification request"}
            </h3>
            <p className="confirm-body">
              This will send a verification request to {employment.verifierEmail}.
            </p>
            <div className="confirm-actions">
              <button
                type="button"
                className="confirm-btn confirm-cancel"
                onClick={closeResendConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm-btn confirm-send"
                onClick={confirmResend}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmploymentCard;
